/* CURRENCY KEEP — ACT 2: THE PHOTOGRAPHS MOVE.
 *
 * PHASE 4, STEP 7. This module reads each photograph out of the owner's
 * own browser and uploads it through the shipping endpoint, which
 * encodes it, verifies it, stores three tiers, and rewrites the row's
 * ref from `idb:` to `r2:`.
 *
 * ══ ⚠⚠⚠ WHAT THIS FILE DOES NOT CONTAIN, AND IT IS THE POINT ═══════
 *
 * THERE IS NO DELETE IN THIS MODULE. Not behind a flag, not behind a
 * confirmation, not in a helper. Act 4 — retiring the local copy — is a
 * SEPARATE PASS on Jesse's standing instruction, 2026-08-24:
 *
 *   *"Seven photographs is small enough that I want to look at them
 *    between the migration and the deletion. The delete guard exists for
 *    exactly that gap — let it be a gap."*
 *
 * ⚠ THAT IS A CONSTRAINT ON THE PLAN, NOT A PREFERENCE ABOUT PACE. The
 * guard in `shared/photo-retire.js` is correct whether or not anyone
 * looked; whether anyone looked is the one thing it cannot check. So the
 * gap is enforced HERE, by this file having no capability, rather than
 * by a caller remembering. A module that could delete on a flag is a
 * module that deletes when a flag is wrong.
 *
 * ══ WHY THIS RUNS IN THE BROWSER AND CANNOT RUN ANYWHERE ELSE ══════
 * An `idb:` ref names bytes in the OWNER'S IndexedDB. No server-side
 * script can reach them, and a headless browser driven by a gate has its
 * own empty profile. The only process that can read these photographs is
 * the page the collector has open. That is why this is a `src/` module
 * and not a `scripts/` one.
 *
 * ══ ⚠⚠ WHAT A PRE-STEP-6 PHOTOGRAPH ACTUALLY HAS, WHICH DECIDES THE
 *       QUALITY OF EVERY ONE OF THE SEVEN ═════════════════════════════
 * `photos.js` stores a note photo as up to three keys:
 *
 *   notevault:photo:<id>:<slot>      the PREVIEW — a data URL. Always.
 *   notevault:photoraw:<id>:<slot>   the ORIGINAL FILE. Step 6 onward.
 *   notevault:photospec:<id>:<slot>  the transform {angle,crop}. Step 6.
 *
 * ⚠ A PHOTO ADDED BEFORE STEP 6 HAS NO SIDECAR — `photos.js` says so in
 * its own header, and 2026-08-21 is when the sidecar started being
 * written. So for those photographs THE PREVIEW IS THE ONLY COPY THAT
 * EXISTS, and the preview is already:
 *
 *   rotated and cropped   the geometry is baked in (`editor.js`)
 *   downscaled to 3200px  max long edge, on a 4200px rotation canvas
 *   JPEG q0.95            stepping down to 0.70 if it exceeded ~4.7 MB
 *
 * ⚠⚠ THE CONSEQUENCE, STATED RATHER THAN DISCOVERED IN THE ARCHIVE TIER
 * TWO YEARS FROM NOW: an archive tier encoded from that preview is a
 * re-encode of an already-lossy 3200px image, not a 4000px 4:4:4 encode
 * of the camera's file. It is the best that exists — the original was
 * never kept — but it is NOT what the tier contract describes, and this
 * module reports which source each photograph used rather than
 * flattening the difference. `sourceKind` is in every row of the report.
 *
 * ⚠ AND THE TRANSFORM IS NOT RE-APPLIED FOR THOSE. Sending the preview
 * WITH the original's `{angle, crop}` would rotate and crop an image
 * that is already rotated and cropped — trap 2, arriving by way of being
 * helpful. A preview-sourced upload sends NO transform, so the endpoint
 * fills in the identity.
 */

import { getToken } from './auth.js'
import { HASH_KEY, PHOTO_KEY, RAW_KEY, SPEC_KEY } from './photo-keys.js'
import { parsePhotoRef } from '../shared/photo-ref.js'
import { validateTransform } from '../shared/photo-transform.js'

/* The endpoint Act 2 uploads through. Same origin as the app — this runs
 * on the deployment the collector is already using, with their session. */
const UPLOAD_PATH = '/api/photos/upload'

/* The tiers a migrated photograph must answer with afterwards. Imported
 * concepts, not a list this file invents: these are the names the
 * delivery route serves and the delete guard confirms. */
const TIERS = Object.freeze(['thumb', 'display', 'archive'])

/* ── reading a photograph out of the browser ───────────────────────── */

/**
 * Turn `data:image/jpeg;base64,…` into bytes and its declared type.
 *
 * ⚠ IT REFUSES ANYTHING THAT IS NOT A DATA URL rather than coercing.
 * A preview that is somehow a plain URL, or an empty string, or the
 * string "[object Blob]" — the `String(blob)` failure `idb.js` warns
 * about — must be a REFUSAL that names the photograph, not fifteen bytes
 * uploaded as a photo.
 */
export function decodeDataUrl(value) {
  if (typeof value !== 'string' || !value.startsWith('data:')) {
    return { ok: false, error: 'not_a_data_url', message: 'the stored preview is not a data URL' }
  }
  const comma = value.indexOf(',')
  const semi = value.indexOf(';')
  if (comma === -1 || semi === -1 || semi > comma) {
    return { ok: false, error: 'malformed_data_url', message: 'the stored preview has no base64 payload' }
  }
  const contentType = value.slice(5, semi)
  if (!contentType.startsWith('image/')) {
    return { ok: false, error: 'not_an_image', message: `the stored preview declares ${contentType}` }
  }
  let bytes
  try {
    const bin = atob(value.slice(comma + 1))
    bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  } catch (err) {
    return { ok: false, error: 'bad_base64', message: 'the stored preview is not decodable base64' }
  }
  if (bytes.length === 0) {
    return { ok: false, error: 'empty', message: 'the stored preview decoded to zero bytes' }
  }
  return { ok: true, bytes, contentType }
}

/**
 * Everything one slot has locally, and WHICH of the two shapes it is.
 *
 * Returns `{ ok, sourceKind, bytes, contentType, transform, localBytes }`.
 *
 *   sourceKind 'original'  the Step 6 sidecar — the camera's own file
 *                          plus the editor's {angle,crop}. The good case.
 *   sourceKind 'preview'   pre-Step-6 — the canvas JPEG, geometry baked
 *                          in, no transform sent.
 *
 * ⚠ `localBytes` IS THE PREVIEW IN BOTH CASES, ALWAYS. It is what the
 * delete guard's rule 5 compares tiers against, and it is what Act 4
 * would eventually delete — so it must be read from the same place
 * regardless of which source was uploaded, or the confirmation would be
 * about a different object than the deletion.
 */
export async function readLocalPhoto(storage, holdingId, slot) {
  const get = async (key) => {
    try {
      const r = await storage.get(key, false)
      return r && r.value !== undefined && r.value !== null ? r.value : null
    } catch (err) {
      return null
    }
  }

  const preview = await get(PHOTO_KEY(holdingId, slot))
  if (preview === null) {
    return { ok: false, error: 'no_local_preview', message: 'the row claims a photograph and IndexedDB has none' }
  }
  const decodedPreview = decodeDataUrl(preview)
  if (!decodedPreview.ok) return decodedPreview

  const raw = await get(RAW_KEY(holdingId, slot))
  const specRaw = await get(SPEC_KEY(holdingId, slot))
  /* The fourth key (Phase 6.5 step 1). Null for every photograph taken
   * before it existed, and null means "nothing to compare" — never "stale". */
  const hashRaw = await get(HASH_KEY(holdingId, slot))
  const localHash = typeof hashRaw === 'string' && /^[0-9a-f]{64}$/.test(hashRaw) ? hashRaw : null

  /* ⚠ BOTH SIDECARS OR NEITHER. `putPhoto` writes them as a pair and
   * cleans both out if either fails, so a lone one is a torn write. The
   * honest response is to fall back to the preview and SAY SO, rather
   * than upload the original under a transform that is missing — which
   * would store the uncropped, unrotated frame as the note of record. */
  const hasBoth = raw !== null && typeof specRaw === 'string' && specRaw !== ''
  if (hasBoth) {
    let parsed = null
    try {
      parsed = JSON.parse(specRaw)
    } catch (err) {
      parsed = null
    }
    const checked = validateTransform(parsed)
    const isBlob = typeof Blob !== 'undefined' && raw instanceof Blob
    if (checked.ok && isBlob && raw.size > 0) {
      const buf = await raw.arrayBuffer()
      return {
        ok: true,
        sourceKind: 'original',
        bytes: new Uint8Array(buf),
        contentType: raw.type || 'application/octet-stream',
        transform: checked.transform,
        localBytes: decodedPreview.bytes,
        localHash,
        note: 'the camera original plus the editor spec — the encoder does the geometry at full resolution',
      }
    }
  }

  return {
    ok: true,
    sourceKind: 'preview',
    bytes: decodedPreview.bytes,
    contentType: decodedPreview.contentType,
    /* ⚠ NULL, NOT IDENTITY-AS-AN-OBJECT. Sending no transform lets the
     * endpoint fill in its own normalised identity; sending one built
     * here would be this file having an opinion about the contract. */
    transform: null,
    localBytes: decodedPreview.bytes,
    localHash,
    note: raw !== null || specRaw !== null
      ? 'a TORN sidecar — original and spec are not both present, so the preview was used'
      : 'no sidecar — this photograph predates Step 6 and the preview is the only copy that exists',
  }
}

/* ── the work list ─────────────────────────────────────────────────── */

/**
 * Every slot that still needs to move, read off the holdings the app
 * already has.
 *
 * ⚠ IT SELECTS ON THE REF, NOT ON `hasFront`. A boolean says "there is a
 * photograph"; the ref says WHERE it is, and only `idb:` is this
 * module's business. That is also what makes the migration re-runnable:
 * a slot that already moved reads as `r2:` and is not selected again.
 *
 * ⚠ AND A MALFORMED REF IS REPORTED, NEVER SKIPPED. `{scheme:'malformed'}`
 * means the row claims a photograph whose ref cannot be read — the exact
 * case `photo-ref.js` refuses to collapse into "empty". Silently
 * skipping it would migrate six of seven and report success.
 */
export function planMigration(collection) {
  const todo = []
  const skipped = []
  for (const h of collection ?? []) {
    for (const slot of ['front', 'back']) {
      const ref = slot === 'front' ? h.frontRef : h.backRef
      const has = slot === 'front' ? h.hasFront : h.hasBack
      const parsed = parsePhotoRef(ref)

      if (parsed.scheme === 'idb') { todo.push({ holdingId: h.id, slot, ref }); continue }
      if (parsed.scheme === 'r2') { skipped.push({ holdingId: h.id, slot, ref, why: 'already in the bucket' }); continue }
      if (parsed.scheme === 'malformed') { skipped.push({ holdingId: h.id, slot, ref, why: '*** MALFORMED REF — this row claims a photograph and the ref cannot be read ***' }); continue }

      /* scheme null — no photo. Only worth reporting if the boolean
       * disagrees, which would mean the row and the app's own view of it
       * have drifted. */
      if (has) skipped.push({ holdingId: h.id, slot, ref, why: '*** hasFront/hasBack says there IS a photograph and the ref is NULL ***' })
    }
  }
  return { todo, skipped }
}

/* ── one photograph ────────────────────────────────────────────────── */

/**
 * Move exactly one slot. Returns a verdict; never throws for an ordinary
 * failure, because a thrown error mid-list is how a run stops having a
 * report.
 *
 * ── ⚠⚠ WHY A FAILURE HERE IS SAFE, AND IT IS A PROPERTY OF THE
 *        ENDPOINT RATHER THAN OF THIS FILE ─────────────────────────────
 * `photo-upload.js` writes the `r2:` ref onto the row only AFTER the
 * tiers are encoded, verified and stored. So every way this can fail —
 * a dropped connection, a 500 from the encoder, a refused verification —
 * leaves the ref saying `idb:` and the photograph exactly where it was.
 * There is no half-migrated state to repair: a slot is either still
 * local, or it is in the bucket with the row pointing at it.
 */
/* ⚠ `signal` AND `onUploadProgress` ARE NEW IN PHASE 6.5 STEP 1, 2026-09-13,
 * AND BOTH ARE OPTIONAL. The signal is handed straight to `fetchImpl`; the
 * progress hook is handed to it too, and a plain `fetch` ignores an option
 * it does not know. `photo-xhr.js` is the `fetchImpl` that honours both.
 * Nothing about what is sent, or what is checked on the way back, changed. */
export async function migrateOne({ storage, holdingId, slot, fetchImpl = fetch, tokenImpl = getToken, signal = null, onUploadProgress = null }) {
  const started = Date.now()
  const verdict = { holdingId, slot, ok: false, sourceKind: null, error: null, message: null, ms: 0 }

  const local = await readLocalPhoto(storage, holdingId, slot)
  if (!local.ok) {
    return { ...verdict, error: local.error, message: local.message, ms: Date.now() - started }
  }
  verdict.sourceKind = local.sourceKind
  verdict.note = local.note
  verdict.sentBytes = local.bytes.length
  verdict.sentType = local.contentType
  verdict.localHash = local.localHash

  const token = await tokenImpl()
  if (!token) {
    return { ...verdict, error: 'no_session', message: 'not signed in — the upload endpoint reads your identity off your own token', ms: Date.now() - started }
  }

  const params = new URLSearchParams({ holding: holdingId, slot })
  if (local.transform) params.set('transform', JSON.stringify(local.transform))

  let res
  try {
    res = await fetchImpl(`${UPLOAD_PATH}?${params}`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': local.contentType },
      body: local.bytes,
      signal,
      onUploadProgress,
    })
  } catch (err) {
    /* ⚠ A THROWN FETCH IS A REFUSAL, NOT A CRASH — and critically, it is
     * not knowledge that the upload did not happen. The request may have
     * been received. The ref is the only thing that decides, and the ref
     * is not written until everything else succeeded.
     *
     * ⚠⚠ `cancelled` IS A DIFFERENT VERDICT FROM `network`, 2026-09-13.
     * Both mean "the request may have been received"; only one of them is
     * the collector's own act (a replacement, a Remove, a sign-out), and
     * nothing must speak to them about a thing they just did. What the
     * server does with the body it may already hold is UNMEASURED — the
     * generation check in `uploadSlot` and the local hash are what make
     * the outcome safe under either answer. */
    const aborted = err?.name === 'AbortError' || signal?.aborted === true
    return { ...verdict, error: aborted ? 'cancelled' : 'network', message: String(err?.message ?? err), ms: Date.now() - started }
  }

  let body = null
  try { body = await res.json() } catch (err) { body = null }

  if (res.status !== 200 || !body?.ok) {
    return {
      ...verdict,
      error: body?.error ?? `http_${res.status}`,
      message: body?.message ?? `the endpoint answered HTTP ${res.status}`,
      ms: Date.now() - started,
    }
  }

  /* ⚠⚠ THE RESPONSE IS NOT TAKEN AS THE ANSWER. A 200 saying `ok:true`
   * is the endpoint's account of itself; the ref on the row is what the
   * rest of the app will read. It is checked here because the one thing
   * this module exists to produce is a row that no longer says `idb:`. */
  const ref = body?.ref ?? null
  const parsed = parsePhotoRef(ref)
  if (parsed.scheme !== 'r2') {
    return {
      ...verdict,
      error: 'ref_not_rewritten',
      message: `the upload reported success and the ref is ${JSON.stringify(ref)} — expected r2:`,
      ms: Date.now() - started,
    }
  }

  /* ⚠ A STAND-IN RESPONSE IS A FAILED MIGRATION, AND IT IS CAUGHT HERE
   * RATHER THAN LEFT TO THE DELETE GUARD. Rules 5 and 6 would refuse to
   * confirm it later, which is the safety net; this is the diagnosis.
   * `standIn: true` means the collector's own bytes were written under
   * three tier names and nothing was encoded. */
  if (body.standIn === true || body.encoder === 'stand-in') {
    return {
      ...verdict,
      error: 'stand_in_encoder',
      message: '*** this deployment is running the STAND-IN encoder — the bytes were stored untiered. See BUILD_NOTES, the standing warning. ***',
      ref,
      ms: Date.now() - started,
    }
  }

  /* ⚠ THE SERVER'S HASH IS CHECKED AGAINST OUR OWN READING OF THE SAME
   * BYTES — 2026-09-13, rule 12. `contentHash` is the endpoint's account
   * of what it hashed; the local hash was computed by `putPhoto` over the
   * bytes this call just sent. They must agree, and a disagreement is
   * refused rather than applied: a ref for bytes we did not send is a ref
   * we cannot vouch for. ⚠ ONLY WHEN A LOCAL HASH EXISTS — a photograph
   * from before the fourth key has nothing to compare, and nothing to
   * compare is not a mismatch. */
  const serverHash = typeof body.contentHash === 'string' ? body.contentHash.toLowerCase() : null
  if (local.localHash && serverHash && serverHash !== local.localHash) {
    return {
      ...verdict,
      error: 'hash_mismatch',
      message: `the endpoint hashed ${serverHash.slice(0, 8)}… and the bytes sent hash ${local.localHash.slice(0, 8)}… — the ref was not applied`,
      ref,
      ms: Date.now() - started,
    }
  }

  return {
    ...verdict,
    ok: true,
    ref,
    encoder: body.encoder ?? null,
    source: body.source ?? null,
    /* ⚠ `transform.outputSize`, NOT a top-level `outputSize` — there
     * is no such field. Read off the contract rather than guessed at;
     * this project has shipped two guessed field names already. */
    outputSize: body.transform?.outputSize ?? null,
    appliedTransform: body.transform?.applied ?? null,
    verified: body.verification?.checked === true,
    tiers: body.tiers ?? null,
    /* ⚠⚠ STEP 4.5b — THE READING IS CARRIED, NOT CONSULTED. The endpoint
     * has answered with `read` since 2.5 and this verdict DROPPED it, so
     * the reading crossed the wire and died here. **That is the 2.5
     * shape one layer up**, where `encodeViaContainer` returned named
     * fields and `read` was not among them.
     * ⚠ It is copied out as the server wrote it — no field inspected,
     * normalised or believed. `null` when the container predates 2.4 or
     * when the Pages side asked for no reading (a back slot, by his
     * front-only trigger). **Nothing in this module reads it.** */
    read: body.read ?? null,
    ms: Date.now() - started,
  }
}

/* ══ ONE UPLOADER — THE QUEUE AND THE PER-SLOT REGISTRY ═══════════════
 *
 * PHASE 6.5 STEP 1, 2026-09-13, HIS RULING: *"The photo uploads at save,
 * not on the next boot. The boot sweep stays as the retry for what
 * instant missed. A progress bar shows the upload; a new edit on the same
 * slot cancels the in-flight upload and starts fresh."*
 *
 * Both paths — the collector's save and the boot sweep — come through
 * `uploadSlot`, so there is exactly one place that decides whether a slot
 * is already on its way, whether a landing is the one the collector is
 * waiting for, and how many encodes the container is asked for at once.
 *
 * ── ⚠ CONCURRENCY ONE, FOR THE SWEEP'S OWN MEASURED REASON ───────────
 * Concurrent 24 MP encodes on a single container instance are untested
 * and this is not the operation to test them in. The queue runs one
 * upload at a time across both paths; the collector's edit goes to the
 * HEAD so a boot backlog never makes them wait behind it.
 *
 * ── ⚠⚠⚠ THE RACE HIS 2026-08-28 REFUSAL NAMED, AND HOW IT IS CLOSED ──
 * *"…a race nobody has tested."* Read off `photo-upload.js` on 2026-09-13:
 * the endpoint PATCHes the ref UNCONDITIONALLY, so two uploads of one slot
 * (A, then the collector's replacement B) landing in the wrong order leave
 * the row pointing at the OLD photograph while the device shows B. (⚠ No
 * good tier set is deleted by this — the delete step keys on the ref as
 * read at the START of each request, and both read `idb:`.)
 *
 * IT IS CLOSED BY ORDER, NOT BY HEALING. Concurrency one means B is never
 * sent while A is still being answered, so the only way A could land
 * after B is if A's transport had been cut off and the server finished
 * anyway. That is the case the abort rule below removes:
 *
 *   replace while QUEUED      A is cancelled before a byte is sent.
 *   replace while SENDING     A is aborted at the transport. The server
 *                             does not hold a whole body, so (⚠ assumed,
 *                             measured on the walk — 1.5) nothing lands.
 *   replace while PROCESSING  A is NOT aborted. The server already holds
 *                             every byte and will finish regardless; an
 *                             abort would only free this side to race it.
 *                             A runs to its answer, which is STALE by
 *                             generation and never touches memory; B is
 *                             queued directly behind it and lands after.
 *
 * ⚠ So "cancels the in-flight upload" is honoured at the transport while
 * the bytes are moving, and honoured by DISCARDING THE RESULT once they
 * have arrived. ✅ RULED 2026-09-13, his words: "A replacement aborts at
 * the transport while bytes are moving and discards the answer once they
 * have all left. Aborting a request the server already holds in full does
 * not stop the server; it only makes the outcome less predictable." So
 * this IS the ruling, not a deviation from it — do not "fix" the
 * processing row above into an abort.
 *
 *   if the tab dies       in the sending window with a partial body the
 *                         server could somehow encode, memory is gone and
 *                         the row could say `r2:` for bytes this device
 *                         does not hold. The local hash (`HASH_KEY`) — the
 *                         sweep's second pass, `staleRemoteSlots` — is the
 *                         net under that, and under anything else that
 *                         leaves a row and a device disagreeing.
 */
const inFlight = new Map()      /* slotKey → { key, gen, controller, phase, sent, total } — the CURRENT entry per slot */
const superseded = new Set()    /* entries a replacement displaced that have not settled yet — still running or queued */
const latestGen = new Map()     /* slotKey → the generation most recently requested */
const statusListeners = new Set()
const slotKeyOf = (holdingId, slot) => `${holdingId}:${slot}`

/** What the screen may say about a slot right now, or null when nothing
 *  is in flight. `phase` is `queued` · `sending` · `processing`. */
export function uploadStatus(holdingId, slot) {
  const r = inFlight.get(slotKeyOf(holdingId, slot))
  return r ? { phase: r.phase, sent: r.sent, total: r.total, gen: r.gen } : null
}

/** Subscribe to status changes. The listener is called with
 *  `(holdingId, slot, status)` — `status` null when the slot settled. */
export function onUploadStatus(fn) {
  statusListeners.add(fn)
  return () => statusListeners.delete(fn)
}

function notifyStatus(holdingId, slot) {
  const s = uploadStatus(holdingId, slot)
  for (const fn of statusListeners) {
    try { fn(holdingId, slot, s) } catch (err) { /* a reporter must not stop an upload */ }
  }
}

/* ⚠ EVERY ENTRY THAT HAS NOT SETTLED — the current one per slot AND the
 * ones a replacement displaced. A superseded upload left to finish its
 * processing is out of the registry but still on the wire and still in
 * the queue; Remove and sign-out must be able to reach it, or the queue
 * stays held behind a request nobody wants. Found by the twin's case 2. */
function* unsettled() {
  yield* inFlight.values()
  yield* superseded
}

/** Abort every unsettled upload for a holding, or for one of its slots. */
export function abortSlotUploads(holdingId, slot = null) {
  let n = 0
  const want = slot === null ? null : slotKeyOf(holdingId, slot)
  for (const r of unsettled()) {
    if (want === null ? r.key.startsWith(`${holdingId}:`) : r.key === want) { r.controller.abort(); n++ }
  }
  return n
}

/** Abort everything. Sign-out calls this — a landing must not mutate a
 *  collection that belongs to whoever signs in next. */
export function abortAllUploads() {
  let n = 0
  for (const r of unsettled()) { r.controller.abort(); n++ }
  return n
}

/* ⚠ THE QUEUE. `head: true` puts a task in front of everything queued but
 * never interrupts the one running — the container is mid-encode on it. */
const queue = []
let pumping = false
function enqueue(task, head, signal = null) {
  return new Promise((resolve) => {
    const item = { task, resolve }
    if (head) queue.unshift(item); else queue.push(item)
    /* ⚠ A TASK CANCELLED WHILE STILL QUEUED LEAVES THE QUEUE AT ONCE and
     * answers `cancelled` now, rather than waiting for its turn to say
     * so — a replacement's caller must not wait behind a boot backlog to
     * learn that the thing it replaced was never sent. */
    if (signal) {
      signal.addEventListener('abort', () => {
        const at = queue.indexOf(item)
        if (at === -1) return
        queue.splice(at, 1)
        resolve({ ok: false, error: 'cancelled', message: 'replaced before it was sent', ms: 0 })
      }, { once: true })
    }
    pump()
  })
}
async function pump() {
  if (pumping) return
  pumping = true
  try {
    while (queue.length) {
      const { task, resolve } = queue.shift()
      let v
      try { v = await task() } catch (err) { v = { ok: false, error: 'threw', message: String(err?.message ?? err) } }
      resolve(v)
    }
  } finally {
    pumping = false
  }
}

/**
 * The SHA-256 the endpoint computes over the body, computed here over the
 * same bytes. Hex, lower case, 64 characters — `r2Ref`'s own shape.
 * Returns null where WebCrypto is unavailable, and null means "no hash",
 * which `readLocalPhoto` treats as nothing to compare.
 */
export async function contentHashOf(bytes) {
  const subtle = globalThis.crypto?.subtle
  if (!subtle || !bytes) return null
  const buf = bytes instanceof ArrayBuffer ? bytes : bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
  const digest = await subtle.digest('SHA-256', buf)
  let out = ''
  for (const b of new Uint8Array(digest)) out += b.toString(16).padStart(2, '0')
  return out
}

/**
 * Upload one slot through the queue, with a generation and an abort.
 *
 *   replace    true — the collector's edit: supersede whatever is in
 *              flight for this slot (aborted while queued or sending; run
 *              to a discarded answer while processing) and start fresh.
 *              false — the sweep: a slot already in flight answers
 *              `in_flight` and is neither sent twice nor counted.
 *   head       true — go to the front of the queue.
 *   after      a promise to wait on before sending — the `idb:` row write
 *              `putPhoto` issued, so a landing's `r2:` PATCH cannot be
 *              overwritten by an earlier-serialised full replace.
 *   onChange   called once after a CURRENT landing mutated memory.
 *
 * Returns `migrateOne`'s verdict plus `gen` and `stale`.
 */
export async function uploadSlot({
  storage, collection, holdingId, slot,
  replace = false, head = false, after = null,
  fetchImpl = fetch, tokenImpl = getToken,
  onChange = null,
  /* his ruling of 2026-10-22: the slot's ref BEFORE this photo, as the
   * editor read it ahead of `putPhoto`; carried onto the verdict untouched
   * (`previousRef`) so `applyReading` can tell a replacement from a first
   * photo. Data only — nothing in this module reads it. */
  previousRef = null,
}) {
  const key = slotKeyOf(holdingId, slot)
  const incumbent = inFlight.get(key)
  if (incumbent && !replace) {
    return { holdingId, slot, ok: false, error: 'in_flight', message: 'this slot is already being uploaded', gen: incumbent.gen, stale: false, ms: 0 }
  }
  /* ⚠ THE ABORT RULE — see the header. Only while the bytes have not all
   * left; once the server holds the body the answer is discarded instead.
   * Either way the incumbent is kept in `superseded` until it settles, so
   * Remove and sign-out can still reach it. */
  if (incumbent) superseded.add(incumbent)
  if (incumbent && incumbent.phase !== 'processing') incumbent.controller.abort()

  const gen = (latestGen.get(key) ?? 0) + 1
  latestGen.set(key, gen)
  const controller = new AbortController()
  const entry = { key, gen, controller, phase: 'queued', sent: 0, total: 0 }
  inFlight.set(key, entry)
  notifyStatus(holdingId, slot)

  const run = async () => {
    if (after) { try { await after } catch (err) { /* the row write's own failure is already toasted by save() */ } }
    if (controller.signal.aborted) {
      return { holdingId, slot, ok: false, error: 'cancelled', message: 'replaced before it was sent', ms: 0 }
    }
    entry.phase = 'sending'
    notifyStatus(holdingId, slot)
    return migrateOne({
      storage, holdingId, slot, fetchImpl, tokenImpl,
      signal: controller.signal,
      onUploadProgress: (sent, total) => {
        entry.sent = sent
        entry.total = total
        /* The bytes are all sent; what follows is the container's encode
         * and store, which reports nothing. The bar says so honestly by
         * having no number in this phase. */
        if (total > 0 && sent >= total) entry.phase = 'processing'
        notifyStatus(holdingId, slot)
      },
    })
  }

  const verdict = await enqueue(run, head, controller.signal)
  verdict.holdingId = holdingId
  verdict.slot = slot
  const stale = latestGen.get(key) !== gen
  if (inFlight.get(key) === entry) inFlight.delete(key)
  superseded.delete(entry)
  verdict.gen = gen
  verdict.stale = stale
  verdict.previousRef = previousRef == null ? null : String(previousRef)

  /* ══ ⚠⚠⚠ THE IN-MEMORY ROW IS UPDATED THE MOMENT THE REF CHANGES ═════
   * `sync_holdings` is a FULL REPLACE built from `state.collection`. The
   * endpoint has just rewritten this row's ref to `r2:` in Postgres, and
   * this tab's copy of that holding still says `idb:`. The next ordinary
   * `save()` — from anywhere in the app, for any reason — writes the
   * stale value back over it.
   *
   * ⚠ `mapper.js:refOut` DOES NOT SAVE US HERE. It preserves an `r2:` ref
   * the holding ALREADY carries; this holding does not carry one yet.
   *
   * ⚠ THE OBJECT IS MUTATED IN PLACE because `state.collection` holds
   * these same objects by reference.
   *
   * ⚠⚠ AND ONLY FOR THE CURRENT GENERATION. A stale landing is a ref for
   * a photograph the collector has already replaced; putting it in memory
   * would make the next save assert the OLD picture. The replacement is
   * queued behind it and its own landing is what memory takes. */
  if (verdict.ok && verdict.ref) {
    const h = (collection ?? []).find((x) => x.id === holdingId)
    if (!stale) {
      if (h) {
        if (slot === 'front') h.frontRef = verdict.ref
        else h.backRef = verdict.ref
        verdict.inMemoryUpdated = true
      } else {
        verdict.inMemoryUpdated = false
        verdict.warning = '*** the holding is not in this collection — the next save will revert this ref ***'
      }
      if (onChange) { try { onChange(verdict) } catch (err) { /* a reporter must not stop an upload */ } }
    } else {
      verdict.inMemoryUpdated = false
    }
  }
  notifyStatus(holdingId, slot)
  return verdict
}

/**
 * The sweep's second pass — `r2:` slots whose LOCAL HASH exists and is
 * not the hash in the ref. That is the state a tab death in the race
 * window leaves behind; nothing else selects it, because `planMigration`
 * selects on `idb:` and the row says `r2:`.
 *
 * ⚠⚠ AN ABSENT HASH IS NEVER PENDING. Every photograph taken before the
 * fourth key has none. `storage.get` answering null, or throwing, is
 * "nothing to compare", and this pass sends nothing for it.
 */
export async function staleRemoteSlots(storage, collection) {
  const out = []
  for (const h of collection ?? []) {
    for (const slot of ['front', 'back']) {
      const ref = slot === 'front' ? h.frontRef : h.backRef
      const parsed = parsePhotoRef(ref)
      if (parsed.scheme !== 'r2') continue
      let hash = null
      try {
        const r = await storage.get(HASH_KEY(h.id, slot), false)
        hash = r && typeof r.value === 'string' && /^[0-9a-f]{64}$/.test(r.value) ? r.value : null
      } catch (err) { hash = null }
      if (hash && parsed.hash && hash !== parsed.hash) {
        out.push({ holdingId: h.id, slot, ref, why: 'the row points at a different photograph than this device holds' })
      }
    }
  }
  return out
}

/* ── the run ───────────────────────────────────────────────────────── */

/**
 * Move every `idb:` photograph, ONE AT A TIME.
 *
 * ── ⚠ SEQUENTIAL, AND THE REASON IS NOT POLITENESS ─────────────────
 * Three concurrent 24 MP encodes against a single container instance is
 * a way to discover its memory ceiling during the one operation in this
 * phase nobody wants to be interesting. Seven photographs gain nothing
 * from concurrency, and sequential gives a failure a well-defined
 * boundary: N moved, this one did not, the rest untouched.
 *
 * ── ⚠⚠ IT CONTINUES PAST A FAILURE, DELIBERATELY ────────────────────
 * Stopping on the first would be right if a failure could compound. It
 * cannot: each slot is independent, the endpoint is all-or-nothing per
 * photograph, nothing is deleted, and a failed slot keeps its `idb:` ref
 * and is simply selected again on the next run. So continuing costs
 * nothing and buys the thing the run is for — knowing the state of ALL
 * seven from one pass instead of learning about them one session at a
 * time.
 *
 * `onProgress` is called after each verdict so a caller can render as it
 * goes rather than after the last one.
 */
/* ⚠ `todo` MAY BE HANDED IN, 2026-09-13. The sweep now builds its list from
 * two passes (`idb:` refs, and `r2:` refs whose local hash disagrees) and
 * passes it here; a caller that passes nothing gets `planMigration`'s
 * list as before. */
export async function migratePhotos({ storage, collection, onProgress = null, fetchImpl = fetch, tokenImpl = getToken, todo: todoIn = null } = {}) {
  const planned = planMigration(collection)
  const todo = todoIn ?? planned.todo
  const skipped = planned.skipped
  const results = []
  const startedAll = Date.now()

  for (const item of todo) {
    /* ⚠ THROUGH THE QUEUE, NOT AROUND IT — 2026-09-13. `uploadSlot` owns
     * the generation, the abort, and the in-memory mutation that used to
     * live in this loop (the comment above it moved with it). The sweep
     * never replaces: a slot the collector's edit already holds answers
     * `in_flight`, and the sweep goes on to the next one. */
    const verdict = await uploadSlot({ storage, collection, holdingId: item.holdingId, slot: item.slot, fetchImpl, tokenImpl, replace: false, head: false })
    results.push(verdict)
    if (onProgress) {
      try { onProgress(verdict, results.length, todo.length) } catch (err) { /* a reporter must not stop a migration */ }
    }
  }

  const moved = results.filter((r) => r.ok)
  return {
    attempted: todo.length,
    moved: moved.length,
    failed: results.length - moved.length,
    skipped,
    results,
    ms: Date.now() - startedAll,
    /* ⚠ SAID ON EVERY RUN RATHER THAN ASSUMED READ. */
    localCopiesDeleted: 0,
    note: 'Act 2 only. Nothing local was deleted — Act 4 is a separate pass, and the gap is deliberate.',
  }
}

/* ── the boot sweep ────────────────────────────────────────────────── */

/**
 * ⚠⚠⚠ REFUSALS THAT CANNOT SUCCEED ON A LATER BOOT, AND SO MUST NOT BE
 * SPOKEN ABOUT ON EVERY BOOT. Currently one: the row claims a photograph
 * and IndexedDB has none, so there are no bytes to send from here or
 * anywhere. **Membership is about whether RETRYING COULD EVER HELP** —
 * not about how bad the error is. A 502 from the encoder is worse and
 * belongs nowhere near this set, because tomorrow it might work.
 */
const UNMIGRATABLE = new Set(['no_local_preview'])

/* ⚠ NOT SPOKEN ABOUT EITHER, FOR THE OPPOSITE REASON — 2026-09-13. A slot
 * the collector's own edit is already uploading (`in_flight`), or one
 * their edit just replaced mid-flight (`cancelled`), is being retried
 * RIGHT NOW by the thing that displaced it. Counting it would tell them
 * a photograph is stuck while they watch its bar move. */
const SILENT = new Set(['in_flight', 'cancelled'])

/**
 * ══ ⚠⚠⚠ PHASE 4 STEP 7, BULLET 4 — THE MISSING WRITE ════════════════
 *
 * **THE HALF OF STEP 7 THAT WAS NEVER BUILT.** Step 7 moved the existing
 * photographs to R2 and swapped the READS. Nothing swapped the WRITE:
 * `photos.js` mints an `idb:` ref on every save, and until this function
 * existed the app's ONLY uploader was `window.__ckAct2` — a console
 * command. A photograph taken and never typed about stayed in one
 * browser for ever.
 *
 * **JESSE CHOSE OPTION B — MIGRATE ON BOOT — 2026-08-28**, over
 * upload-on-save and over both. His reasons, in his order: it is the only
 * option that drains the backlog by construction; the code already exists
 * and Act 2 proved it on real data; and it retires `__ckAct2` by
 * PROMOTION rather than leaving a console command as the only uploader.
 *
 * ⚠⚠ SUPERSEDED IN PART BY HIS RULING OF 2026-09-13 (Phase 6.5 step 1):
 * the upload is now INSTANT at save, and THIS SWEEP IS THE RETRY for what
 * instant missed. His 2026-08-28 refusal of "both" — *"buys seconds
 * instead of one app start, in exchange for a race nobody has tested"* —
 * is not overruled; it is the specification: the race is named and closed
 * at `uploadSlot` (generation) and `staleRemoteSlots` (the local hash),
 * and `photo-instant-check` watches both. His other refusal stands in
 * full and is the reason this sweep survives: *"a slot that misses its
 * single attempt is local-only forever with nothing to sweep it up."*
 *
 * ══ ⚠⚠⚠ IT MUST NOT BE AWAITED BY THE BOOT, AND THAT IS MEASURED ═════
 *
 * One upload is **~10 s** — 10,038 ms cold and 10,091 ms warm, measured
 * 2026-08-28 on a real 3.00 MB HEIC through the real Function. **Cold and
 * warm are 53 ms apart**, so this is not a warm-up cost that goes away;
 * it is the intrinsic price of encode-and-store, and a backlog of N is
 * ~10N seconds. **Awaiting this in `bootSession` would freeze the app
 * behind it.** The caller fires it and moves on; that is not a style
 * choice and reversing it is a regression with a number attached.
 *
 * ⚠ SILENT ON SUCCESS. `onDone` is called only when something did NOT
 * move. A sweep that worked is not news, and a toast on every boot is
 * noise a collector learns to dismiss — which would cost exactly the
 * failure notice this exists to deliver.
 *
 * ⚠ THE ORDINARY BOOT COSTS ONE ARRAY SCAN. `planMigration` reads
 * memory, and an empty `todo` returns immediately.
 *
 * ⚠⚠ AN EARLIER VERSION OF THIS COMMENT SAID THE EARLY RETURN AVOIDS A
 * NETWORK ROUND TRIP. **IT DOES NOT, AND THE SABOTAGE SUITE CAUGHT THE
 * OVERCLAIM.** `migratePhotos` runs `planMigration` itself and its loop
 * does not execute on an empty `todo`, so no token is fetched and no
 * request is made with or without this line. What the early return
 * actually buys is a **distinct `ran: false`** — so "there was nothing
 * to do" cannot be read as "everything failed" — and one redundant
 * scan. Corrected rather than left standing: a comment claiming a saving
 * that is not there is how the next reader gets a wrong cost model.
 *
 * @param onDone  called ONLY on failure, with `{failed, moved}`
 * @param onChange called after a successful move so the caller can
 *                 re-render — the pending badge must clear without a
 *                 reload, or the state lies in the other direction
 */
export async function sweepPendingPhotos({
  storage, collection, onDone = null, onChange = null,
  fetchImpl = fetch, tokenImpl = getToken,
} = {}) {
  /* Two passes since 2026-09-13: `idb:` refs, and `r2:` refs whose local
   * hash says the row points at a different photograph than this device
   * holds (the tab-death half of the race — see `uploadSlot`). */
  const planned = planMigration(collection ?? [])
  const stale = await staleRemoteSlots(storage, collection ?? [])
  const todo = planned.todo.concat(stale)
  /* ⚠ THE EARLY RETURN IS THE COMMON CASE, not an optimisation for a
   * rare one: once the backlog is drained every subsequent boot lands
   * here. It reports `ran: false` rather than a zeroed result so a
   * caller cannot mistake "nothing to do" for "everything failed". */
  if (!todo.length) return { ran: false, attempted: 0, moved: 0, failed: 0 }

  let moved = 0
  const out = await migratePhotos({
    storage,
    collection,
    fetchImpl,
    tokenImpl,
    todo,
    /* ⚠⚠ NO try/catch AROUND `onChange` HERE, AND THAT IS DELIBERATE.
     * `migratePhotos` ALREADY wraps every `onProgress` call — *"a
     * reporter must not stop a migration"* — so a guard here would be a
     * second one, and **neither could then be sabotaged independently**:
     * disabling either leaves the other swallowing the throw, so the
     * property would look protected while nothing proved it.
     *
     * ⚠ FOUND BY THE SABOTAGE SUITE, NOT BY READING. Case 4 was written
     * against a guard here, went GREEN under sabotage, and the redundancy
     * is why. The protection now lives in exactly one place and case 4
     * patches that place. */
    onProgress: (v) => {
      if (!v.ok) {
        /* ⚠⚠ THE CONSOLE HALF, WHICH WAS SPECIFIED AND THEN NOT BUILT.
         * The first writing returned here and the per-slot reasons went
         * NOWHERE — so the sweep's first real run reported "2 failed"
         * and there was no way to learn why without the `__ckAct2`
         * scaffolding that had deliberately not been deleted yet.
         * The operator detail belongs here; the toast gets a count. */
        console.warn(`photo sweep: ${v.holdingId}/${v.slot} — ${v.error}: ${v.message}`)
        return
      }
      moved++
      /* Re-render per slot rather than once at the end: on a long
       * backlog the collector watches badges clear instead of waiting. */
      if (onChange) onChange(v)
    },
  })

  /* ══ ⚠⚠⚠ A REFUSAL THAT CAN NEVER SUCCEED IS NOT A FAILED UPLOAD ════
   *
   * **FOUND ON THE SWEEP'S FIRST REAL RUN, 2026-08-28.** It reported
   * *"2 photographs are still only on this device"* about two `idb:`
   * refs that have no bytes on any device. Jesse: *"that wording is
   * wrong for this case and it's the first thing a real collector would
   * hit."*
   *
   * ⚠⚠ AND THE WORDING IS THE SMALLER HALF. Those two refs can NEVER
   * succeed, so counting them meant the toast fired **on every single
   * app start, for ever**, until he re-photographs that note. That is
   * exactly the noise failure his condition on this notice was written
   * against — `photo-sweep-sabotage` case 2 — arriving through a door
   * the fixtures could not see, because every failure they model is
   * retryable.
   *
   * ⚠ THEY ARE NOT LEFT UNREPORTED. A slot whose ref is `idb:` with no
   * local bytes classifies as `MISSING` and already renders the amber
   * *"Photograph not on this device"* marker on that note — permanent,
   * per-slot, and actionable in a way a boot toast is not. The console
   * line above carries the reason for an operator.
   *
   * ⚠⚠ THE SPLIT IS BY WHAT THE REFUSAL MEANS, NOT BY ITS TEXT. Anything
   * not named here — a 502 from the encoder, a dropped connection — is
   * retryable BY DEFAULT and still spoken about, so a new failure mode
   * cannot go silent by being unrecognised. */
  const unmigratable = out.results.filter((r) => !r.ok && UNMIGRATABLE.has(r.error))
  const silent = out.results.filter((r) => !r.ok && SILENT.has(r.error))
  const retryable = out.results.filter((r) => !r.ok && !UNMIGRATABLE.has(r.error) && !SILENT.has(r.error))

  if (retryable.length > 0 && onDone) {
    try { onDone({ failed: retryable.length, moved: out.moved }) } catch (err) { /* likewise */ }
  }
  return {
    ran: true,
    attempted: out.attempted,
    moved: out.moved,
    failed: out.failed,
    retryable: retryable.length,
    unmigratable: unmigratable.length,
    silent: silent.length,
    stale: stale.length,
    results: out.results,
  }
}

/* ── what to look at afterwards ────────────────────────────────────── */

/**
 * Fetch all three tiers of one migrated photograph back OUT of the
 * bucket, through the shipping delivery route.
 *
 * ⚠ THROUGH `/api/photos/…`, NOT OFF THE UPLOAD RESPONSE. The upload
 * response is the encoder's account of what it made; this is what the
 * app will actually serve, authorised the way a reader is authorised.
 * They are different claims, and only the second one is the one a
 * collector experiences.
 */
export async function fetchTiers({ holdingId, slot, fetchImpl = fetch, tokenImpl = getToken }) {
  const token = await tokenImpl()
  const out = {}
  for (const tier of TIERS) {
    try {
      const res = await fetchImpl(`/api/photos/note/${holdingId}/${slot}/${tier}`, {
        headers: { authorization: `Bearer ${token}` },
      })
      if (!res.ok) { out[tier] = { ok: false, status: res.status }; continue }
      const buf = new Uint8Array(await res.arrayBuffer())
      out[tier] = {
        ok: true,
        status: res.status,
        bytes: buf.length,
        contentType: res.headers.get('content-type'),
        /* The two bytes nothing else in this project looks at. A tier
         * truncated in the bucket is a valid JPEG with correct
         * dimensions and no end marker — A2's trap, and the reason
         * `photo-retire.js` checks the LAST two bytes. */
        soi: buf[0] === 0xff && buf[1] === 0xd8,
        eoi: buf[buf.length - 2] === 0xff && buf[buf.length - 1] === 0xd9,
        blobUrl: URL.createObjectURL(new Blob([buf], { type: res.headers.get('content-type') ?? 'image/jpeg' })),
      }
    } catch (err) {
      out[tier] = { ok: false, error: String(err?.message ?? err) }
    }
  }
  return out
}

/**
 * ⚠⚠ THE THING JESSE ASKED FOR: SOMETHING TO LOOK AT IN THE GAP.
 *
 * Act 2 moves bytes and rewrites refs. It does NOT swap the app's read
 * path — that is still `Then` in the Step 7 plan — so after a successful
 * migration every screen in the app still renders the LOCAL PREVIEW. The
 * migrated photographs would be invisible.
 *
 * ⚠ SO "LOOK AT THEM BETWEEN THE MIGRATION AND THE DELETION" NEEDS A
 * SURFACE THAT DOES NOT EXIST YET, AND THIS IS IT. For every migrated
 * slot it puts the local preview beside all three tiers fetched back out
 * of the bucket, at the size they really are, and states the two-byte
 * check under each. It is the only place the two copies can be compared
 * by eye before one of them is deleted.
 *
 * It builds an overlay and touches nothing else — no route, no state, no
 * screen. Closing it removes it.
 */
export async function contactSheet({ collection, fetchImpl = fetch, tokenImpl = getToken, mount = null } = {}) {
  const migrated = []
  for (const h of collection ?? []) {
    for (const slot of ['front', 'back']) {
      const ref = slot === 'front' ? h.frontRef : h.backRef
      if (parsePhotoRef(ref).scheme === 'r2') migrated.push({ holdingId: h.id, slot, ref })
    }
  }

  const host = mount ?? document.createElement('div')
  host.setAttribute('data-ck-contact-sheet', '')
  host.style.cssText = 'position:fixed;inset:0;z-index:99999;overflow:auto;background:#11131a;color:#e8e8ea;font:13px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;padding:20px'
  if (!mount) document.body.appendChild(host)

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
  const head = document.createElement('div')
  head.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:16px;margin-bottom:16px">
    <strong style="font-size:15px">ACT 2 CONTACT SHEET — ${migrated.length} migrated photograph(s)</strong>
    <button data-close style="background:#2a2d3a;color:#e8e8ea;border:1px solid #3a3d4a;border-radius:6px;padding:6px 12px;cursor:pointer">close</button>
  </div>
  <div style="opacity:.7;margin-bottom:18px">Local preview on the left, the three stored tiers fetched back through <code>/api/photos/…</code> on the right. Nothing local has been deleted.</div>`
  host.appendChild(head)
  head.querySelector('[data-close]')?.addEventListener('click', () => host.remove())

  if (migrated.length === 0) {
    const none = document.createElement('div')
    none.textContent = 'No r2: refs in this vault — nothing has been migrated yet.'
    host.appendChild(none)
    return host
  }

  for (const item of migrated) {
    const row = document.createElement('div')
    row.style.cssText = 'border:1px solid #2a2d3a;border-radius:8px;padding:12px;margin-bottom:14px'
    row.innerHTML = `<div style="margin-bottom:8px"><strong>${esc(item.holdingId)}</strong> · ${esc(item.slot)} · <span style="opacity:.7">${esc(item.ref)}</span></div><div data-body style="opacity:.6">loading tiers…</div>`
    host.appendChild(row)

    const tiers = await fetchTiers({ holdingId: item.holdingId, slot: item.slot, fetchImpl, tokenImpl })

    let localUrl = null
    try {
      const r = await window.storage.get(PHOTO_KEY(item.holdingId, item.slot), false)
      localUrl = r?.value ?? null
    } catch (err) { localUrl = null }

    const cell = (label, src, caption, warn) => `
      <figure style="margin:0;flex:0 0 auto;max-width:260px">
        <div style="background:#0b0c11;border:1px solid ${warn ? '#a33' : '#2a2d3a'};border-radius:6px;padding:6px;min-height:120px;display:flex;align-items:center;justify-content:center">
          ${src ? `<img src="${src}" alt="${esc(label)}" style="max-width:100%;max-height:240px;display:block">` : '<span style="opacity:.5">—</span>'}
        </div>
        <figcaption style="margin-top:6px;font-size:12px;${warn ? 'color:#f88' : 'opacity:.75'}">
          <strong>${esc(label)}</strong><br>${caption}
        </figcaption>
      </figure>`

    const cells = [cell('local preview', localUrl, localUrl ? 'still in IndexedDB — not deleted' : 'no local preview found', !localUrl)]
    for (const tier of TIERS) {
      const t = tiers[tier]
      if (!t?.ok) {
        cells.push(cell(tier, null, t?.status ? `HTTP ${t.status}` : esc(t?.error ?? 'failed'), true))
        continue
      }
      const whole = t.soi && t.eoi
      cells.push(cell(
        tier,
        t.blobUrl,
        `${(t.bytes / 1024).toFixed(0)} KB · ${esc(t.contentType ?? '?')}<br>SOI ${t.soi ? '✓' : '✗'} · EOI ${t.eoi ? '✓' : '✗'}${whole ? '' : ' — NOT WHOLE'}`,
        !whole,
      ))
    }
    row.querySelector('[data-body]').outerHTML = `<div style="display:flex;gap:12px;flex-wrap:wrap;align-items:flex-start">${cells.join('')}</div>`
  }

  return host
}
